// PixelMandala.jsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faLightbulb, faCheckCircle, faQuestionCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

export default function PixelMandala({ gridSize = 6, youtubeLink }) {
  const [showHelp, setShowHelp] = useState(false);
  const [notification, setNotification] = useState(null);

  // Generate random solution for demo
  const generateSolution = (size) => {
    return Array(size)
      .fill(null)
      .map(() => Array(size).fill(null).map(() => (Math.random() > 0.5 ? 1 : 0)));
  };

  const SOLUTION = generateSolution(gridSize);

  const getClues = (solution) => {
    const rows = solution.map(row => {
      let clue = [];
      let count = 0;
      row.forEach(cell => {
        if (cell === 1) count++;
        else if (count > 0) { clue.push(count); count = 0; }
      });
      if (count > 0) clue.push(count);
      return clue.length ? clue : [0];
    });

    const cols = solution[0].map((_, colIndex) => {
      let clue = [];
      let count = 0;
      solution.forEach(row => {
        if (row[colIndex] === 1) count++;
        else if (count > 0) { clue.push(count); count = 0; }
      });
      if (count > 0) clue.push(count);
      return clue.length ? clue : [0];
    });

    return { rows, cols };
  };

  const { rows: rowClues, cols: colClues } = getClues(SOLUTION);

  const [grid, setGrid] = useState(Array(gridSize).fill(null).map(() => Array(gridSize).fill(false)));
  const [history, setHistory] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [highlight, setHighlight] = useState({ row: null, col: null });

  const toggleCell = (r, c) => {
    const newGrid = grid.map(row => [...row]);
    newGrid[r][c] = !newGrid[r][c];
    setHistory([...history, grid]);
    setGrid(newGrid);
  };

  const undo = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setGrid(last);
  };

  const giveHint = () => {
    const emptyCells = [];
    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        if ((grid[r][c] ? 1 : 0) !== SOLUTION[r][c]) emptyCells.push([r, c]);
      }
    }
    if (emptyCells.length === 0) return;
    const [r, c] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    const newGrid = grid.map(row => [...row]);
    newGrid[r][c] = SOLUTION[r][c] === 1;
    setHistory([...history, grid]);
    setGrid(newGrid);
  };

const checkSolution = () => {
    const isCorrect = grid.every((row, rIdx) =>
      row.every((cell, cIdx) => (cell ? 1 : 0) === SOLUTION[rIdx][cIdx])
    );
    setCompleted(isCorrect);
    if (!isCorrect) {
      // Show beautiful notification
      setNotification({ type: 'error', message: '❌ Not correct yet. Keep trying!' });

      // Auto-hide after 3 seconds
      setTimeout(() => setNotification(null), 3000);
    }
  };

  return (
    <div className="p-2 min-h-screen bg-black text-gray-300 flex flex-col items-center">
      
      {/* Top Buttons */}
      <div className="w-full flex justify-center gap-4 items-center mb-4">
        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 flex justify-center items-center gap-2"
          onClick={() => window.history.back()}
        >
          ← Back
        </button>

        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-blue-600 text-black rounded-lg hover:bg-blue-500 flex justify-center items-center gap-2"
          onClick={() => setShowHelp(true)}
        >
          <FontAwesomeIcon icon={faQuestionCircle} /> Help
        </button>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4">
          <div className="bg-gray-900 p-6 rounded-xl max-w-lg w-full text-gray-300 flex flex-col gap-4">
            <h3 className="text-m lg:text-xl font-bold text-green-400">📖 How to Play Pixel-Mandala</h3>
            <p className='text-m lg:text-lg'>
              Pixel-Mandala is a relaxing Nonogram puzzle. Fill the grid cells according to the row and column clues.
              Each number indicates consecutive filled cells. Use hints and undo buttons to assist your progress.
            </p>
            <p>Steps:</p>
            <ul className="list-disc list-inside text-gray-400">
              <li>Tap a cell to fill or unfill it.</li>
              <li>Check the clues at the top (columns) and left (rows).</li>
              <li>Use the Hint button to fill a correct cell.</li>
              <li>Undo previous moves with the Undo button.</li>
              <li>Press Check when you think the puzzle is complete.</li>
            </ul>

            {youtubeLink && (
              <a
                href={youtubeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-red-600 text-black rounded-lg hover:bg-red-500 text-center font-semibold"
              >
                ▶ Watch
              </a>
            )}

            <button
              className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 mt-2"
              onClick={() => setShowHelp(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {completed && (
        <div className="text-green-400 text-xl flex items-center gap-2 mb-2 animate-pulse">
          <FontAwesomeIcon icon={faCheckCircle} /> Puzzle Completed!
        </div>
      )}

      <h2 className="text-lg lg:text-2xl font-bold mb-2 text-green-400">🎨 Pixel-Mandala (Nonogram Light)</h2>
      <p className="text-gray-400 mb-4 text-center max-w-md text-xs lg:text-m">
        Tap the cells to fill them according to the clues. Use hints and undo to complete the pattern!
      </p>

      {/* Grid */}
      <div className="flex flex-col items-center">
        <div className="flex">
          <div className="w-10 h-10"></div>
          {colClues.map((clue, i) => (
            <div key={i} className={`w-12 h-12 flex flex-col items-center justify-end text-sm text-gray-400 ${highlight.col === i ? 'bg-gray-800' : ''}`}>
              {clue.join("\n")}
            </div>
          ))}
        </div>

        {grid.map((row, rIdx) => (
          <div key={rIdx} className="flex">
            <div className={`w-10 h-12 flex items-center justify-center text-sm text-gray-400 ${highlight.row === rIdx ? 'bg-gray-800' : ''}`}>
              {rowClues[rIdx].join(" ")}
            </div>
            {row.map((cell, cIdx) => (
              <div
                key={cIdx}
                onClick={() => toggleCell(rIdx, cIdx)}
                onMouseEnter={() => setHighlight({ row: rIdx, col: cIdx })}
                onMouseLeave={() => setHighlight({ row: null, col: null })}
                className={`w-12 h-12 border border-gray-600 m-0.5 cursor-pointer transition ${cell ? 'bg-green-400' : 'bg-gray-900 hover:bg-gray-800'}`}
              ></div>
            ))}
          </div>
        ))}
      </div>

      {/* Buttons */}
      <div className="p-4 min-h-screen bg-black text-gray-300 flex flex-col items-center relative">     
        {/* Notification */}
        {notification && (
            <div
                className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg text-sm font-semibold ${
                notification.type === 'error' ? 'bg-red-700 text-white' : 'bg-green-600 text-black'
                } flex justify-between items-center shadow-lg z-50 w-full`}
            >
                <span>{notification.message}</span>
                <button
                className="w-10 p-1 text-lg flex items-center justify-center bg-red-800"
                onClick={() => setNotification(null)}
                >
                <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        )}

        <div className="mt-2 flex flex-row gap-4 flex-wrap justify-center">
            <button
            onClick={checkSolution}
            className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-green-600 text-black font-semibold rounded-lg hover:bg-green-500 transition flex justify-center items-center gap-2"
            >
            <FontAwesomeIcon icon={faCheckCircle} /> Check
            </button>
            <button
            onClick={undo}
            className="w-20 text-xs lg:text-m lg:w-40  px-2 py-2 bg-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-600 transition flex justify-center items-center gap-2"
            >
            <FontAwesomeIcon icon={faUndo} /> Undo
            </button>
            <button
            onClick={giveHint}
            className="w-20 text-xs lg:text-m lg:w-40  px-2 py-2 bg-blue-600 text-black font-semibold rounded-lg hover:bg-blue-500 transition flex justify-center items-center gap-2"
            >
            <FontAwesomeIcon icon={faLightbulb} /> Hint
            </button>
        </div>
       </div>
    </div>
  );
}
